import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import PageHeader from "../../components/PageHeader";
import SoftBox from "../../components/SoftBox";
import { UseCompanyWorkspaces, UseCompanyColors, UseSelectedWorkspace } from "../../hooks/company";
import { UPDATE_GROUP } from "../../api/group";
import { getWorkspaces, setSelectedWorkspace } from "../../redux/workspace/actions";
import { GET_DASHBOARDS, UPDATE_DASHBOARD } from "../CompanyMetrics/dashboards/dashboardStore";
import TvViewScreen from "./TvViewScreen";
import TvViewModal from "./TvViewModal";
import { Skeleton } from "../../components/shared/Loading";

/**
 * TV View — every TV view the company has, lined up.
 *
 * A TV view is company-level: the built-in board (Team Leaderboard + Team
 * Pulse, its own two pages) plus every dashboard switched on with "Show on TV"
 * (each its own view, its own page). Views never merge.
 *
 * Click a view to choose which workspaces can use it and whether it is dark or
 * light on the wall. Team ranking (average per player) is NOT part of a view —
 * it is a property of a workspace — so it lives behind its own button.
 */

const BRAND = "var(--gmb-primary, #1B598E)";

const CSS = `
.tvv{ font-family:"DM Sans", sans-serif; color:#0F172A; }

/* header actions */
.tvv-acts{ display:flex; gap:9px; flex-wrap:wrap; }
.tvv-btn{ display:inline-flex; align-items:center; gap:7px; font-family:"DM Sans"; font-size:12.5px; font-weight:500;
  color:#0F172A; background:#fff; border:1px solid rgba(15,23,42,0.10); border-radius:8px; padding:9px 14px; cursor:pointer;
  white-space:nowrap; transition:border-color .13s, color .13s; }
.tvv-btn:hover{ border-color:${BRAND}; color:${BRAND}; }
.tvv-btn.p{ background:${BRAND}; border-color:${BRAND}; color:#fff; }
.tvv-btn.p:hover{ color:#fff; filter:brightness(1.07); }
.tvv-btn:disabled{ opacity:.55; cursor:default; }
.tvv-btn svg{ width:14px; height:14px; }

/* the wall */
/* stretch — every card in a row shares the tallest one's height, so no view is
   ever taller than its neighbours even if its meta somehow runs longer. */
.tvv-grid{ display:grid; grid-template-columns:repeat(4,1fr); gap:14px; align-items:stretch; }
@media (max-width:1200px){ .tvv-grid{ grid-template-columns:repeat(2,1fr); } }
@media (max-width:700px){ .tvv-grid{ grid-template-columns:1fr; } }
.tvv-card{ position:relative; background:#fff; border:1px solid rgba(15,23,42,0.10); border-radius:12px; padding:10px;
  width:100%; text-align:left; font-family:"DM Sans"; display:block; transition:border-color .15s, box-shadow .15s; }
.tvv-card:hover{ border-color:#D2DBE6; box-shadow:0 10px 26px -14px rgba(16,24,40,0.26); }
/* The preview is the click target; the kebab sits over it, so the card itself
   is a plain div — a button inside a button is invalid and swallows the menu. */
.tvv-open-hit{ display:block; width:100%; padding:0; border:0; background:transparent; cursor:pointer; border-radius:8px; }

/* ⋯ — every action a view has, so nothing is hidden behind a guess. */
.tvv-kebab{ position:absolute; top:16px; right:16px; z-index:6; width:26px; height:26px; border-radius:7px;
  display:grid; place-items:center; background:rgba(255,255,255,0.96); border:1px solid rgba(15,23,42,0.10);
  color:#667085; cursor:pointer; box-shadow:0 2px 6px rgba(16,24,40,0.10); transition:.14s; }
.tvv-kebab:hover, .tvv-kebab.on{ border-color:${BRAND}; color:${BRAND}; background:#fff; }
.tvv-kebab svg{ width:15px; height:15px; }
.tvv-menu{ position:absolute; top:47px; right:16px; z-index:20; width:214px; background:#fff;
  border:1px solid rgba(15,23,42,0.10); border-radius:10px; box-shadow:0 18px 40px -12px rgba(16,24,40,0.26); padding:6px; }
.tvv-menu button{ display:flex; align-items:center; gap:10px; width:100%; border:0; background:transparent;
  font-family:"DM Sans"; font-size:12.5px; color:#0F172A; padding:8px 9px; border-radius:7px; cursor:pointer; text-align:left; }
.tvv-menu button:hover:not(:disabled){ background:#F5F8FB; }
.tvv-menu button svg{ width:14px; height:14px; color:#98A2B3; flex:none; }
.tvv-menu button:hover:not(:disabled) svg{ color:${BRAND}; }
.tvv-menu button.danger{ color:#D92D20; }
.tvv-menu button.danger svg{ color:#D92D20; }
.tvv-menu button:disabled{ opacity:.42; cursor:default; }
.tvv-menu .sep{ height:1px; background:#F1F4F8; margin:5px 4px; }
.tvv-chip.thm{ color:#475467; background:#fff; border:1px solid rgba(15,23,42,0.10);
  display:inline-flex; align-items:center; gap:4px; }
.tvv-chip.thm svg{ width:10px; height:10px; }
.tvv-cinfo{ padding:12px 5px 4px; }
/* Name on its own line (truncated, never wraps) so a long name can never push
   the chips onto an extra row and make this card taller than its neighbours. */
.tvv-cnm{ display:block; font-size:14.5px; font-weight:600; letter-spacing:-0.01em;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.tvv-chips{ display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin-top:7px; }
.tvv-chip{ font-size:10.5px; font-weight:600; color:#98A2B3; background:#F2F5F9; border-radius:5px; padding:3px 8px; }
.tvv-chip.b{ color:${BRAND}; background:rgba(27,89,142,0.09); }
.tvv-cwho{ margin:7px 0 0; font-size:12.5px; color:#475467; display:flex; align-items:center; gap:6px; }
.tvv-cwho svg{ width:13px; height:13px; color:#98A2B3; flex:none; }

.tvv-add{ border:1.5px dashed #CFD9E5; border-radius:12px; background:rgba(255,255,255,0.55); display:flex;
  flex-direction:column; align-items:center; justify-content:center; gap:8px; min-height:200px; padding:24px;
  text-align:center; font-family:"DM Sans"; cursor:pointer; }
.tvv-add:hover{ border-color:${BRAND}; background:#fff; }
.tvv-add .ic{ width:36px; height:36px; border-radius:10px; background:rgba(27,89,142,0.09); color:${BRAND};
  display:grid; place-items:center; }
.tvv-add .ic svg{ width:17px; height:17px; }
.tvv-add b{ font-size:13.5px; font-weight:600; }
.tvv-add p{ margin:0; font-size:12px; color:#475467; max-width:30ch; line-height:1.5; }

.tvv-empty{ background:#fff; border:1px solid rgba(15,23,42,0.10); border-radius:12px; padding:40px 24px; text-align:center; }
.tvv-empty h3{ margin:0 0 6px; font-size:15px; font-weight:600; }
.tvv-empty p{ margin:0; font-size:13px; color:#475467; line-height:1.6; }

/* Modal chrome lives in TvViewModal (the house Dialog). What follows is
   only the content inside it. */
.tvv-open{ display:flex; align-items:center; justify-content:center; gap:8px; width:100%; margin-top:16px;
  font-family:"DM Sans"; font-size:13px; font-weight:500; color:${BRAND}; background:rgba(27,89,142,0.07);
  border:1px solid rgba(27,89,142,0.18); border-radius:8px; padding:11px 14px; cursor:pointer; transition:background .13s; }
.tvv-open:hover{ background:rgba(27,89,142,0.12); }
.tvv-open svg{ width:14px; height:14px; }
.tvv-lbl{ font-size:12.5px; font-weight:600; margin:0 0 8px; }
.tvv-hint{ margin:7px 0 0; font-size:11.5px; color:#98A2B3; line-height:1.5; }
.tvv-grp + .tvv-grp{ margin-top:18px; padding-top:18px; border-top:1px solid rgba(15,23,42,0.08); }

.tvv-seg{ display:inline-flex; background:rgba(15,23,42,0.05); border-radius:8px; padding:3px; gap:2px; flex-wrap:wrap; }
.tvv-seg button{ display:inline-flex; align-items:center; gap:6px; font-family:"DM Sans"; font-size:12.5px; font-weight:500;
  color:#475467; background:transparent; border:0; border-radius:6px; padding:7px 12px; cursor:pointer; }
.tvv-seg button.on{ background:#fff; color:${BRAND}; font-weight:600; box-shadow:0 1px 3px rgba(16,24,40,0.10); }

.tvv-list{ border:1px solid rgba(15,23,42,0.10); border-radius:8px; overflow:hidden; max-height:252px; overflow-y:auto; background:#fff; }
.tvv-row{ display:flex; align-items:center; gap:10px; width:100%; border:0; background:#fff; font-family:"DM Sans";
  font-size:13px; color:#0F172A; padding:10px 12px; cursor:pointer; text-align:left; }
.tvv-row + .tvv-row{ border-top:1px solid rgba(15,23,42,0.06); }
.tvv-row:hover{ background:#F8FAFC; }
.tvv-row .tx{ flex:1; min-width:0; }
.tvv-row .tx small{ display:block; font-size:11px; color:#98A2B3; margin-top:1px; }
.tvv-row .sub{ padding-left:12px; }
.tvv-tick{ width:16px; height:16px; border-radius:5px; border:1.5px solid #CFD8E3; background:#fff; flex:none; position:relative; }
.tvv-tick.on{ background:${BRAND}; border-color:${BRAND}; }
.tvv-tick.on::after{ content:""; position:absolute; left:4.5px; top:1.5px; width:4px; height:8px;
  border:solid #fff; border-width:0 1.7px 1.7px 0; transform:rotate(45deg); }
/* Inline "replace the Default, or show both" decision — plain off-white, no tint. */
.tvv-decide{ margin:2px 12px 10px 26px; padding:11px 13px; background:#F7F8FA; border:1px solid #E7EBF0; border-radius:10px; }
.tvv-decide-t{ font-size:12.5px; color:#475467; line-height:1.5; margin:0; }
.tvv-decide-btns{ display:flex; gap:8px; margin-top:9px; flex-wrap:wrap; }
.tvv-decide-btns button{ font:600 12.5px "DM Sans"; border-radius:8px; padding:7px 12px; cursor:pointer;
  border:1px solid #E4E8EE; background:#fff; color:#344054; transition:background .14s, border-color .14s, color .14s; }
/* Secondary (Replace…) buttons: subtle grey hover. */
.tvv-decide-btns button.dc-rep:hover,
.tvv-decide-btns button:not(.dc-both):not(.dc-cancel):hover{ background:#F1F4F8; border-color:#D6DCE5; }
/* Primary "Show both": stays a real blue on hover (the grey hover was making it look disabled). */
.tvv-decide-btns .dc-both{ background:${BRAND}; border-color:transparent; color:#fff; }
.tvv-decide-btns .dc-both:hover{ background:var(--gmb-primary-deep, #164C79); border-color:transparent; color:#fff; }
.tvv-decide-btns .dc-cancel{ border-color:transparent; background:transparent; color:#98A2B3; }
.tvv-decide-btns .dc-cancel:hover{ background:#EFF2F6; color:#475467; }
.tvv-sw{ width:34px; height:20px; border-radius:999px; border:0; padding:0; position:relative; cursor:pointer;
  background:rgba(15,23,42,0.18); flex:none; transition:background .16s; }
.tvv-sw.on{ background:${BRAND}; }
.tvv-sw:disabled{ opacity:.55; cursor:default; }
.tvv-sw::after{ content:""; position:absolute; top:2px; left:2px; width:16px; height:16px; border-radius:50%;
  background:#fff; box-shadow:0 1px 3px rgba(0,0,0,0.22); transition:left .16s; }
.tvv-sw.on::after{ left:16px; }
.tvv-locked{ display:flex; align-items:center; gap:9px; border:1px solid rgba(15,23,42,0.10); background:#F8FAFC;
  border-radius:8px; padding:11px 12px; font-size:13px; color:#475467; line-height:1.45; }
/* Icons in this panel are 15px — an unsized inline SVG renders at its
   intrinsic size and swallows the row. */
.tvv-locked > svg{ width:15px; height:15px; flex:none; color:#98A2B3; }
.tvv-search{ display:flex; align-items:center; gap:8px; height:36px; padding:0 12px; margin-bottom:9px;
  border:1px solid rgba(15,23,42,0.10); border-radius:8px; background:#fff; }
.tvv-search svg{ width:14px; height:14px; color:#98A2B3; flex:none; }
.tvv-search input{ border:0; outline:0; background:transparent; width:100%; font-family:"DM Sans"; font-size:13px; color:#0F172A; }
`;

const IcPeople = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  </svg>
);
const IcTv = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4" />
  </svg>
);
const IcRank = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M4 6h10M4 12h13M4 18h8" /><circle cx="20" cy="6" r="1.4" />
  </svg>
);

const IcDots = (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <circle cx="12" cy="5" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="12" cy="19" r="1.7" />
  </svg>
);
const IcSun = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);
const IcEdit = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
const IcTvOff = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4M3 3l18 18" />
  </svg>
);
const IcMoon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
  </svg>
);

/* "TVs by workspace" panel — one row per workspace, chips are the views its TV plays. */
const BYWS_CSS = `
.tvv-row.byws{ align-items:center; gap:14px; }
.byws-chips{ display:flex; align-items:center; gap:7px; flex-wrap:wrap; justify-content:flex-end; margin-left:auto; }
.byws-chip{ display:inline-flex; align-items:center; gap:6px; background:#EEF3FA; border:1px solid #D7E2F0;
  border-radius:8px; padding:5px 6px 5px 10px; font-size:12.5px; font-weight:600; color:#1B598E; white-space:nowrap; }
.byws-chip button{ border:0; background:transparent; color:#7D93AE; font-size:15px; line-height:1; cursor:pointer;
  padding:0 2px; border-radius:4px; }
.byws-chip button:hover{ background:#DCE6F3; color:#B4471F; }
.byws-empty{ font-size:12.5px; color:#B0B7C3; font-style:italic; }
.byws-addwrap{ position:relative; display:inline-flex; }
.byws-add{ border:1.3px dashed #C6D0DC; background:transparent; color:#1B598E; border-radius:8px;
  padding:5px 10px; font-size:12.5px; font-weight:600; cursor:pointer; white-space:nowrap; }
.byws-add:hover{ background:#EEF3FA; border-color:#1B598E; }
.byws-menu{ position:absolute; right:0; top:calc(100% + 6px); background:#fff; border:1px solid #E4E8EE;
  border-radius:10px; box-shadow:0 14px 34px rgba(15,23,42,.16); padding:5px; z-index:30; min-width:190px; display:block; }
.byws-menu button{ display:block; width:100%; text-align:left; border:0; background:transparent; cursor:pointer;
  font-size:13px; font-weight:500; color:#0F172A; padding:8px 10px; border-radius:7px; white-space:nowrap; }
.byws-menu button:hover{ background:#F1F4F8; }
.byws-full{ font-size:11.5px; font-weight:600; color:#8A5A12; background:#FBEFE2; border:1px solid #EAD3A8;
  border-radius:999px; padding:3px 9px; white-space:nowrap; }
`;

const THEME_ICON = { follow: IcTv, dark: IcMoon, light: IcSun };

const THEMES = [
  { id: "follow", label: "Auto" },
  { id: "dark", label: "Dark" },
  { id: "light", label: "Light" },
];

const TvView = () => {
  const allWorkspaces = UseCompanyWorkspaces(true);
  // Stable identity — the hook returns a fresh [] when redux is still empty,
  // which would re-run every memo below on every render.
  const workspaces = useMemo(() => allWorkspaces || [], [allWorkspaces]);
  const { primaryColor } = UseCompanyColors() || {};
  const selectedWorkspace = UseSelectedWorkspace();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync: updateGroup } = UPDATE_GROUP();

  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openView, setOpenView] = useState(null); // { id, section } — which panel of a view
  // A workspace being added to a custom view that already shows the Default (or
  // is at the 2-view cap) needs a choice — replace, or show both — resolved
  // inline in the same list instead of a trip to the Default board.
  const [wsDecision, setWsDecision] = useState(null); // wsId awaiting the choice
  const [menuFor, setMenuFor] = useState(null); // which card's ⋯ menu is open
  const [confirmRemove, setConfirmRemove] = useState(null); // view pending "remove from TV" confirmation
  const [rankOpen, setRankOpen] = useState(false);
  const [byWsOpen, setByWsOpen] = useState(false); // "TVs by workspace" panel
  const [addFor, setAddFor] = useState(null); // which workspace's add-menu is open
  const [rankSearch, setRankSearch] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [overrides, setOverrides] = useState({}); // optimistic averaging
  const [defaultOverrides, setDefaultOverrides] = useState({}); // optimistic "shows the default board"

  const load = useCallback(() => {
    setLoading(true);
    GET_DASHBOARDS()
      .then((res) => setBoards((Array.isArray(res.data) ? res.data : []).filter((d) => d.showOnTv)))
      .catch(() => setBoards([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  /* Any click that isn't the ⋯ button or its menu closes the menu. */
  useEffect(() => {
    if (!menuFor) return undefined;
    const onDoc = (e) => {
      if (e.target.closest && e.target.closest(".tvv-menu, .tvv-kebab")) return;
      setMenuFor(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuFor]);

  // Default board is offered unless a workspace opts out.
  const showsDefault = (ws) =>
    defaultOverrides[ws._id] !== undefined ? defaultOverrides[ws._id] : ws.tvShowDefaultBoard !== false;

  const toggleDefaultFor = async (ws) => {
    const next = !showsDefault(ws);
    setDefaultOverrides((p) => ({ ...p, [ws._id]: next }));
    try {
      await updateGroup({ groupId: ws._id, data: { tvShowDefaultBoard: next } });
      dispatch(getWorkspaces());
    } catch (err) {
      setDefaultOverrides((p) => ({ ...p, [ws._id]: !next }));
      toast.error("Could not update that workspace");
    }
  };

  const brandVars = primaryColor ? { "--gmb-primary": primaryColor } : undefined;

  /* ── the views ─────────────────────────────────────────────────────────── */
  const views = useMemo(
    () => [
      {
        id: "builtin",
        builtin: true,
        name: "Default board",
        tag: "Built in",
        pages: 2,
        sub: "Team Leaderboard, then Team Pulse — rotating every 45 seconds.",
        // Which workspaces offer it, stored per workspace (tvShowDefaultBoard).
        wsIds: workspaces
          .filter((w) =>
            defaultOverrides[w._id] !== undefined
              ? defaultOverrides[w._id]
              : w.tvShowDefaultBoard !== false,
          )
          .map((w) => String(w._id)),
      },
      ...boards.map((d) => ({
        id: d._id,
        board: d,
        name: d.name || "Untitled dashboard",
        tag: "From a dashboard",
        pages: 1,
        theme: d.tvTheme || "follow",
        wsIds: (d.tvWorkspaceIds || []).map(String),
      })),
    ],
    [boards, workspaces, defaultOverrides],
  );

  const whoText = (v) => {
    if (v.builtin) {
      const off = workspaces.length - v.wsIds.length;
      if (!off) return "Every workspace uses this view";
      if (!v.wsIds.length) return "No workspace uses this view";
      return `${v.wsIds.length} of ${workspaces.length} workspaces use this view`;
    }
    // "Every workspace" is the full list of workspace ids. Empty = unassigned:
    // a new view belongs to nobody until a workspace is picked here, so it does
    // NOT default onto every TV (the built-in default board already covers all).
    const allSelected = workspaces.length > 0 && workspaces.every((w) => v.wsIds.includes(String(w._id)));
    if (allSelected) return "Every workspace uses this view";
    if (!v.wsIds.length) return "Not assigned yet — won't show on any TV";
    const names = v.wsIds
      .map((id) => (workspaces.find((w) => String(w._id) === id) || {}).name)
      .filter(Boolean);
    if (!names.length) return "Not assigned yet — won't show on any TV";
    return `${names.join(", ")} use${names.length === 1 ? "s" : ""} this view`;
  };

  /* ── saving a view ─────────────────────────────────────────────────────── */
  const patchBoard = async (id, patch) => {
    setBoards((prev) => prev.map((d) => (d._id === id ? { ...d, ...patch } : d)));
    try {
      await UPDATE_DASHBOARD(id, patch);
    } catch (err) {
      load();
    }
  };

  const toggleWs = (v, wsId) => {
    const allIds = workspaces.map((w) => String(w._id));
    const allSelected = allIds.length > 0 && allIds.every((id) => v.wsIds.includes(id));
    let next;
    // "Every workspace" toggles the whole list on/off. Empty = unassigned.
    if (wsId === "__all") next = allSelected ? [] : allIds;
    else if (v.wsIds.includes(wsId)) next = v.wsIds.filter((x) => x !== wsId);
    else next = [...v.wsIds, wsId];
    patchBoard(v.id, { tvWorkspaceIds: next });
  };

  /* ── averaging (a workspace property, not a view's) ────────────────────── */
  const isAvg = (ws) => (overrides[ws._id] !== undefined ? overrides[ws._id] : !!ws.tvAveragePerPlayer);
  const avgOn = workspaces.filter(isAvg).length;

  const setAvg = async (ws, next) => {
    setOverrides((p) => ({ ...p, [ws._id]: next }));
    setSavingId(ws._id);
    try {
      await updateGroup({ groupId: ws._id, data: { tvAveragePerPlayer: next } });
      dispatch(getWorkspaces());
    } catch (err) {
      setOverrides((p) => ({ ...p, [ws._id]: !next }));
      toast.error("Could not update that workspace");
    } finally {
      setSavingId(null);
    }
  };

  /* Open a view ON the TV. Two things have to travel with the click or the TV
     falls back to the built-in board: the workspace it should be showing, and
     which view that workspace is watching (the TV reads the view per
     workspace from localStorage). */
  const openTv = (v) => {
    let wsId = selectedWorkspace?._id || null;
    if (!v.builtin && v.wsIds.length && !v.wsIds.includes(String(wsId))) {
      const ws = workspaces.find((w) => String(w._id) === v.wsIds[0]);
      if (ws) {
        wsId = ws._id;
        dispatch(setSelectedWorkspace({ selectedWorkspace: ws }));
      }
    }
    try {
      localStorage.setItem(
        `gmb_tv_view_${wsId || "all"}`,
        v.builtin ? "builtin" : `board:${v.id}`,
      );
    } catch (e) { /* storage unavailable — the TV just opens on its last view */ }
    navigate("/live/leaderboard/tv");
  };

  const active = openView ? views.find((v) => v.id === openView.id) : null;
  const section = openView?.section || "access";

  /* A dashboard view is a WINDOW onto a dashboard that still lives in
     Analytics. Removing it here switches the window off; it never deletes the
     dashboard someone else may be using — that stays in Analytics, where you
     can see it is a dashboard you are deleting. */
  const removeFromTv = (v) => {
    setMenuFor(null);
    setBoards((prev) => prev.filter((d) => d._id !== v.id));
    UPDATE_DASHBOARD(v.id, { showOnTv: false }).catch(() => {
      toast.error("Could not take that view off the TV");
      load();
    });
  };
  const q = rankSearch.trim().toLowerCase();
  const rankRows = workspaces.filter((w) => !q || w.name.toLowerCase().includes(q));

  return (
    <>
      <PageHeader
        headerText="TV View"
        subheaderText="Views that can play on a TV — each one is available to the workspaces it is assigned to"
      />

      <SoftBox
        className="tvv"
        style={brandVars}
        sx={{ margin: { mini: "0.5rem 1rem 1rem 1rem", sm: "1rem 1.5rem 1.5rem 1.5rem" } }}
      >
        <style>{CSS}</style>

        <SoftBox sx={{ display: "flex", justifyContent: "flex-end", mb: "14px" }}>
          <div className="tvv-acts">
            <button type="button" className="tvv-btn" onClick={() => setByWsOpen(true)}>
              {IcPeople}
              TVs by workspace
            </button>
            <button type="button" className="tvv-btn" onClick={() => setRankOpen(true)}>
              {IcRank}
              Ranking settings
            </button>
            <button type="button" className="tvv-btn p" onClick={() => navigate("/company-metrics?tab=dashboards")}>
              {IcTv}
              Add view
            </button>
          </div>
        </SoftBox>

        {loading ? (
          <div className="tvv-grid">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                variant="rect"
                height={190}
                radius={12}
              />
            ))}
          </div>
        ) : (
          <div className="tvv-grid">
            {views.map((v) => {
              const theme = THEMES.find((t) => t.id === (v.theme || "follow")) || THEMES[0];
              const themeIcon = THEME_ICON[v.theme] || THEME_ICON.follow;
              return (
                <div key={v.id} className="tvv-card">
                  <button
                    type="button"
                    className={`tvv-kebab${menuFor === v.id ? " on" : ""}`}
                    aria-label={`Options for ${v.name}`}
                    aria-haspopup="menu"
                    aria-expanded={menuFor === v.id}
                    onClick={() => setMenuFor(menuFor === v.id ? null : v.id)}
                  >
                    {IcDots}
                  </button>
                  {menuFor === v.id && (
                    <div className="tvv-menu" role="menu">
                      <button type="button" role="menuitem" onClick={() => { setMenuFor(null); setOpenView({ id: v.id, section: "access" }); }}>
                        {IcPeople}Workspace access…
                      </button>
                      <button type="button" role="menuitem" onClick={() => { setMenuFor(null); setOpenView({ id: v.id, section: "appearance" }); }}>
                        {IcSun}Appearance…
                      </button>
                      {!v.builtin && (
                        <button type="button" role="menuitem" onClick={() => navigate(`/company-metrics?tab=dashboards&dashboard=${v.id}`)}>
                          {IcEdit}Edit dashboard
                        </button>
                      )}
                      <div className="sep" />
                      <button type="button" role="menuitem" onClick={() => openTv(v)}>
                        {IcTv}Open on TV
                      </button>
                      <div className="sep" />
                      <button
                        type="button"
                        role="menuitem"
                        className={v.builtin ? "" : "danger"}
                        disabled={v.builtin}
                        title={v.builtin ? "The default view can’t be removed — it is what a TV falls back to" : undefined}
                        onClick={() => { setMenuFor(null); setConfirmRemove(v); }}
                      >
                        {IcTvOff}Remove from TV
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    className="tvv-open-hit"
                    onClick={() => setOpenView({ id: v.id, section: "access" })}
                  >
                    <TvViewScreen view={v} />
                  </button>
                  <span className="tvv-cinfo">
                    <b className="tvv-cnm">{v.name}</b>
                    <span className="tvv-chips">
                      <span className={`tvv-chip${v.builtin ? " b" : ""}`}>{v.tag}</span>
                      <span className="tvv-chip">{v.pages} page{v.pages === 1 ? "" : "s"}</span>
                      {!v.builtin && (
                        <span className="tvv-chip thm">
                          {themeIcon}
                          {theme.label}
                        </span>
                      )}
                    </span>
                    <span className="tvv-cwho">{IcPeople}{whoText(v)}</span>
                  </span>
                </div>
              );
            })}
            <div
              className="tvv-add"
              role="button"
              tabIndex={0}
              onClick={() => navigate("/company-metrics?tab=dashboards")}
              onKeyDown={(e) => e.key === "Enter" && navigate("/company-metrics?tab=dashboards")}
            >
              <span className="ic">{IcTv}</span>
              <b>Add view</b>
              <p>Switch on “Show on TV” for a dashboard in Analytics and it appears here.</p>
            </div>
          </div>
        )}
      </SoftBox>

      {/* ── one view: who uses it, how it looks ── */}
      {active && (
        <div className="tvv" style={brandVars}>
          <style>{CSS}</style>
          <TvViewModal
            open
            title={active.name}
            subtitle={section === "appearance" ? "How it looks on the wall." : "Which workspaces can use this view."}
            onClose={() => { setOpenView(null); setWsDecision(null); }}
            onDone={() => { setOpenView(null); setWsDecision(null); }}
            doneLabel="Done"
          >
            {section === "access" && (
            <div className="tvv-grp">
              <p className="tvv-lbl">Which workspaces use this view</p>
              <div className="tvv-list">
                {!active.builtin && (
                  <button type="button" className="tvv-row" onClick={() => toggleWs(active, "__all")}>
                    <span className={`tvv-tick${workspaces.length && workspaces.every((w) => active.wsIds.includes(String(w._id))) ? " on" : ""}`} />
                    <span className="tx">Every workspace<small>Anyone switching workspace on a TV is offered it</small></span>
                  </button>
                )}
                {workspaces.map((w) => {
                  const wsId = String(w._id);
                  const on = active.wsIds.includes(wsId);
                  const others = active.builtin
                    ? []
                    : views.filter((v) => v.id !== active.id && v.wsIds.includes(wsId));
                  const def = others.find((v) => v.builtin);
                  const customs = others.filter((v) => !v.builtin);
                  const atCap = others.length >= 2;
                  const rowClick = () => {
                    if (active.builtin) { toggleDefaultFor(w); return; }
                    if (on) { toggleWs(active, wsId); setWsDecision(null); return; }
                    // Turning it on: if the TV is empty or only holds one custom,
                    // just add it. If the Default is there, or it's at the cap, ask.
                    if (!def && others.length < 2) { toggleWs(active, wsId); return; }
                    setWsDecision(wsId);
                  };
                  const deciding = !active.builtin && wsDecision === wsId;
                  return (
                    <React.Fragment key={w._id}>
                      <button type="button" className="tvv-row" onClick={rowClick}>
                        <span className={`tvv-tick${on ? " on" : ""}`} />
                        <span className={`tx${w.parentId ? " sub" : ""}`} style={{ textTransform: "capitalize" }}>{w.name}</span>
                      </button>
                      {deciding && (
                        <div className="tvv-decide">
                          <p className="tvv-decide-t">
                            {atCap
                              ? `${w.name}'s TV already has two views — the most it holds. Replace one to add “${active.name}”.`
                              : `${w.name} is showing the Default board. Replace it, or show both as tabs?`}
                          </p>
                          <div className="tvv-decide-btns">
                            {!atCap && def && (
                              <button type="button" className="dc-both" onClick={() => { toggleWs(active, wsId); setWsDecision(null); }}>
                                Show both
                              </button>
                            )}
                            {def && (
                              <button type="button" onClick={() => { toggleDefaultFor(w); toggleWs(active, wsId); setWsDecision(null); }}>
                                Replace the Default
                              </button>
                            )}
                            {customs.map((cv) => (
                              <button
                                key={cv.id}
                                type="button"
                                onClick={() => {
                                  patchBoard(cv.id, { tvWorkspaceIds: cv.wsIds.filter((x) => x !== wsId) });
                                  toggleWs(active, wsId);
                                  setWsDecision(null);
                                }}
                              >
                                Replace “{cv.name}”
                              </button>
                            ))}
                            <button type="button" className="dc-cancel" onClick={() => setWsDecision(null)}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              {active.builtin && (
                <p className="tvv-hint">
                  Switch a workspace off and its TV skips the built-in board. Leave at least one view on for
                  each workspace, or that TV has nothing to show.
                </p>
              )}
            </div>
            )}

            {section === "appearance" && (
            <div className="tvv-grp">
              <p className="tvv-lbl">Dark or light on the wall</p>
              {active.builtin ? (
                <>
                  <div className="tvv-locked">Switched on the screen itself, with the sun/moon button.</div>
                  <p className="tvv-hint">The built-in board follows whatever the room sets.</p>
                </>
              ) : (
                <>
                  <div className="tvv-seg">
                    {THEMES.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={active.theme === t.id ? "on" : ""}
                        onClick={() => patchBoard(active.id, { tvTheme: t.id })}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <p className="tvv-hint">Auto uses the sun/moon button on the screen itself.</p>
                </>
              )}
            </div>
            )}

            <button type="button" className="tvv-open" onClick={() => openTv(active)}>
              Open this view on the TV
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </TvViewModal>
        </div>
      )}

      {/* ── team ranking: a workspace property, its own place ── */}
      {rankOpen && (
        <div className="tvv" style={brandVars}>
          <style>{CSS}</style>
          <TvViewModal
            open
            title="Team ranking"
            subtitle="How teams are ranked on the built-in board's leaderboard, per workspace."
            onClose={() => setRankOpen(false)}
            onDone={() => setRankOpen(false)}
            doneLabel="Done"
          >
            {workspaces.length > 8 && (
              <div className="tvv-search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                </svg>
                <input
                  type="text"
                  placeholder="Search workspaces…"
                  value={rankSearch}
                  onChange={(e) => setRankSearch(e.target.value)}
                />
              </div>
            )}
            <div className="tvv-list" style={{ maxHeight: 320 }}>
              {rankRows.map((w) => {
                const on = isAvg(w);
                return (
                  <div key={w._id} className="tvv-row" style={{ cursor: "default" }}>
                    <span className={`tx${w.parentId ? " sub" : ""}`} style={{ textTransform: "capitalize" }}>
                      {w.name}
                      <small style={{ textTransform: "none" }}>{on ? "Average per player" : "Team total"}</small>
                    </span>
                    <button
                      type="button"
                      className={`tvv-sw${on ? " on" : ""}`}
                      role="switch"
                      aria-checked={on}
                      aria-label={`Average per player for ${w.name}`}
                      disabled={savingId === w._id}
                      onClick={() => setAvg(w, !on)}
                    />
                  </div>
                );
              })}
              {!rankRows.length && (
                <div className="tvv-row" style={{ cursor: "default", color: "#98A2B3" }}>No workspaces match that.</div>
              )}
            </div>
            <p className="tvv-hint">
              Averaging ranks teams by average points per player instead of raw totals — fairer when teams are
              different sizes. Player rankings aren&apos;t affected. On for <b>{avgOn}</b> of {workspaces.length}.
            </p>
          </TvViewModal>
        </div>
      )}

      {/* TVs by workspace — every workspace and the views its TV is playing,
          with add/remove right on the row. Opened from the button up top. */}
      {byWsOpen && (
        // Wrapped in `.tvv` so --gmb-primary reaches the modal — without it the
        // primary "Done" button renders with no fill and reads as disabled.
        <div className="tvv" style={brandVars}>
        <TvViewModal
          open
          width={660}
          title="TVs by workspace"
          subtitle="What each workspace's TV is playing. A TV holds up to 2 — they show as tabs on screen."
          onClose={() => { setByWsOpen(false); setAddFor(null); }}
          onDone={() => { setByWsOpen(false); setAddFor(null); }}
          doneLabel="Done"
        >
          <style>{BYWS_CSS}</style>
          <div className="tvv-list" style={{ maxHeight: 430 }}>
            {workspaces.map((w) => {
              const wsId = String(w._id);
              const isOn = (v) => (v.builtin ? showsDefault(w) : v.wsIds.includes(wsId));
              const onViews = views.filter(isOn);
              const avail = views.filter((v) => !isOn(v));
              const full = onViews.length >= 2;
              const drop = (v) => (v.builtin ? toggleDefaultFor(w) : toggleWs(v, wsId));
              return (
                <div key={w._id} className="tvv-row byws" style={{ cursor: "default" }}>
                  {/* No sub-workspace indent here — these rows aren't grouped
                      under their parent, so indenting just looks misaligned. */}
                  <span className="tx" style={{ textTransform: "capitalize" }}>
                    {w.name}
                    <small style={{ textTransform: "none" }}>
                      {onViews.length ? `${onViews.length} of 2` : "Nothing on this TV"}
                    </small>
                  </span>
                  <div className="byws-chips">
                    {onViews.map((v) => (
                      <span key={v.id} className="byws-chip">
                        {v.name}
                        <button type="button" aria-label={`Take ${v.name} off ${w.name}'s TV`} onClick={() => drop(v)}>×</button>
                      </span>
                    ))}
                    {!onViews.length && <span className="byws-empty">blank</span>}
                    {!full && !!avail.length && (
                      <span className="byws-addwrap">
                        <button type="button" className="byws-add" onClick={() => setAddFor(addFor === wsId ? null : wsId)}>+ Add</button>
                        {addFor === wsId && (
                          <span className="byws-menu">
                            {avail.map((v) => (
                              <button key={v.id} type="button" onClick={() => { drop(v); setAddFor(null); }}>{v.name}</button>
                            ))}
                          </span>
                        )}
                      </span>
                    )}
                    {full && <span className="byws-full">Full</span>}
                  </div>
                </div>
              );
            })}
            {!workspaces.length && (
              <div className="tvv-row" style={{ cursor: "default", color: "#98A2B3" }}>No workspaces yet.</div>
            )}
          </div>
        </TvViewModal>
        </div>
      )}

      {/* Same wrapper here — this modal's "Remove from TV" button was losing its
          fill for the same reason. */}
      {confirmRemove && (
        <div className="tvv" style={brandVars}>
        <TvViewModal
          open
          width={440}
          title={`Remove ${confirmRemove.name ? `“${confirmRemove.name}”` : "this view"} from TV?`}
          subtitle="It stops playing on every TV that shows it."
          onClose={() => setConfirmRemove(null)}
          onDone={() => { const v = confirmRemove; setConfirmRemove(null); removeFromTv(v); }}
          doneLabel="Remove from TV"
        >
          <p style={{ fontSize: 13.5, color: "#475467", lineHeight: 1.6, margin: "6px 0 12px" }}>
            This only takes it off the TV rotation — the dashboard itself stays in Analytics, and you can switch it back on any time.
          </p>
        </TvViewModal>
        </div>
      )}

    </>
  );
};

export default TvView;
